import { useState, useEffect } from "react";
import axios from "axios";
import { Check, ChevronsUpDown, Package } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ProductCombobox = ({ value, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    axios.get(`${API}/products`).then((res) => setProducts(res.data)).catch(() => {});
  }, []);

  const grouped = products.reduce((acc, p) => {
    const cat = p.category.toUpperCase();
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          data-testid="product-combobox-trigger"
          className="w-full justify-between h-14 bg-input border-2 border-border hover:border-primary/50 text-lg px-4 font-mono rounded-sm transition-colors"
        >
          {value ? (
            <span className="truncate">{value}</span>
          ) : (
            <span className="text-muted-foreground/50">SELECT PRODUCT...</span>
          )}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
        <Command>
          <CommandInput
            placeholder="Search products..."
            data-testid="product-search-input"
            className="h-12"
          />
          <CommandList>
            <CommandEmpty>
              <div className="flex flex-col items-center gap-2 py-4">
                <Package className="w-8 h-8 text-muted-foreground" />
                <span className="text-sm text-muted-foreground">No product found</span>
              </div>
            </CommandEmpty>
            {Object.entries(grouped).map(([category, items]) => (
              <CommandGroup key={category} heading={category}>
                {items.map((product) => (
                  <CommandItem
                    key={product.id}
                    value={product.name}
                    data-testid={`product-option-${product.name.toLowerCase().replace(/\s/g, "-")}`}
                    onSelect={() => {
                      onSelect(product.name);
                      setOpen(false);
                    }}
                    className="py-3 text-base cursor-pointer"
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        value === product.name ? "opacity-100 text-primary" : "opacity-0"
                      )}
                    />
                    {product.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
